// Een gen-2 Cloud Function met min-instances 0 kan het eerste verzoek na een
// stille periode afbreken ("The request was aborted because there was no
// available instance"); de aanroeper ziet dan 'internal' terwijl de aanroep
// zelf klopt. Eén of twee pogingen later staat de instantie er. Zonder deze
// helper zwijgt het scherm tot de gebruiker de pagina herlaadt (testronde
// 2026-09-15, B7; verwant: inbox #221).

import normalizeCallableCode from './normalizeCallableCode'

const TRANSIENT_CODES = new Set(['internal', 'unavailable', 'deadline-exceeded', 'resource-exhausted'])
const DEFAULT_DELAYS_MS = [2000, 5000]

/**
 * Is dit een fout waarbij opnieuw proberen zin heeft?
 *
 * @param {{code?: string}|null|undefined} error - Fout uit een httpsCallable
 * @returns {boolean}
 */
const isTransientCallableError = (error) => {
    const code = normalizeCallableCode(String(error?.code || ''))
    return TRANSIENT_CODES.has(code)
}

const slaap = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Roept `call` aan en probeert bij een tijdelijke fout opnieuw, met oplopende
 * wachttijden. Een fout die niet tijdelijk is komt meteen bij de aanroeper.
 *
 * @template T
 * @param {(attempt: number) => Promise<T>} call - De aanroep; krijgt het pogingnummer (0 = eerste)
 * @param {object} [opts]
 * @param {number[]} [opts.delaysMs] - Wachttijd vóór elke nieuwe poging; de lengte is het aantal nieuwe pogingen
 * @param {(error: *) => boolean} [opts.isTransient] - Wanneer opnieuw proberen
 * @param {(ms: number) => Promise<void>} [opts.wait] - Injecteerbaar voor tests
 * @returns {Promise<T>}
 */
const callWithRetry = async (call, { delaysMs = DEFAULT_DELAYS_MS, isTransient = isTransientCallableError, wait = slaap } = {}) => {
    let poging = 0
    for (;;) {
        try {
            return await call(poging)
        } catch (error) {
            if (!isTransient(error) || poging >= delaysMs.length) throw error
            await wait(delaysMs[poging])
            poging += 1
        }
    }
}

export { callWithRetry, isTransientCallableError, DEFAULT_DELAYS_MS }
